import { SocketProvider } from '../context/socketContext';
import Login from '../pages/auth/login/Login';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { fetchUserInfo } from '../store/action/userInfo';

// This component sits ABOVE every nested module route (see router/index.js —
// one <PrivateRoute><Layout/></PrivateRoute> wraps the whole `/user/:suburl/*`
// tree, with modules as children rendered through Layout's <Outlet/>). It
// therefore mounts exactly once per hard refresh or fresh entry into the
// authenticated app, and stays mounted while the user switches between
// modules — making it the one place a "run this once per session" fetch
// belongs.
const PrivateRoute = ({ children }) => {
	const location = useLocation();
	const { suburl } = useParams();
	const { userOnboardingMe } = useAuth();
	const dispatch = useDispatch();

	// Selected as PRIMITIVES, one at a time, not as `state.userInfo`.
	//
	// Selecting the whole slice returns a NEW object reference every time
	// anything in it changes — including `userInfo.data`, which the fetch below
	// writes. That re-rendered this component on its own fetch, and since
	// useAuth() hands back a fresh `userOnboardingMe` function on every render,
	// the effect's dependency array changed too and it fired again: fetch →
	// re-render → fetch, hammering /user/info forever. Comparing two strings
	// and a boolean by value breaks that cycle at the source.
	const authToken = useSelector((state) => state.userInfo.authToken);
	const isOnboardingCompleted = useSelector((state) => state.userInfo.isOnboardingCompleted);

	// Second, independent guard. Even if something re-renders this component
	// for an unrelated reason, the authoritative user fetch is a once-per-mount
	// job — PrivateRoute mounts once per hard refresh and stays mounted across
	// module navigation, so there is never a legitimate reason to repeat it.
	const hasSyncedUser = useRef(false);

	const [isChecking, setIsChecking] = useState(true);
	const isLoginRoute = location.pathname === `/user/${suburl}`;

	useEffect(() => {
		const checkOnboarding = async () => {
			if (isLoginRoute) {
				setIsChecking(false);
				return;
			}

			if (!authToken) {
				setIsChecking(false);
				return;
			}

			// Nothing else guaranteed this ran after a refresh. `authToken` and
			// `isOnboardingCompleted` above come straight from redux-persist's
			// replay of whatever was last written to the `userInfo` slice — but
			// the actual user object (`userInfo.userInfo.data`: first_name,
			// role.role_permissions, organisation_id, ...) was only ever
			// refreshed by a handful of individual pages calling this same
			// action on their own mount (Dashboard, Invoices, Roles, ...).
			// Refreshing on any OTHER page — Inventory, Purchases, Reports —
			// left the persisted (possibly stale, or shaped differently by the
			// login response — see the userInfo reducer's LOGIN_DONE branch)
			// value in place indefinitely. That is what produced "Welcome!
			// undefined undefined" in the header and, more seriously, made
			// every hasPermission(key, user?.role?.role_permissions) check
			// across every module silently evaluate against `[]` — disabling
			// Create/Export/Import buttons and row navigation with no error.
			// One authoritative refetch here, before any module renders, fixes
			// all of that regardless of which page the refresh happened on —
			// which is also why switching modules afterwards didn't help
			// before: nothing on the module-switch path re-fetched either.
			try {
				if (!hasSyncedUser.current) {
					// Set BEFORE awaiting, not after. Two effect runs can overlap
					// while the request is in flight, and a flag set on completion
					// would let both through — which is the loop this guards.
					hasSyncedUser.current = true;
					await dispatch(fetchUserInfo());
				}
			} catch (err) {
				// A failed refresh must not force a logout — USER_INFO_GET_FAIL
				// only clears `userInfo`, never `authToken` — so this can only
				// leave the user on the same "incomplete" state a transient
				// network blip would have left them in before this fix existed,
				// not a new failure mode.
				console.error('Failed to refresh user info:', err);
			}

			if (isOnboardingCompleted) {
				setIsChecking(false);
				return;
			}

			try {
				await userOnboardingMe();
			} catch (err) {
				console.error('Onboarding check failed:', err);
			} finally {
				setIsChecking(false);
			}
		};

		checkOnboarding();
	}, [authToken, isOnboardingCompleted, isLoginRoute, userOnboardingMe, dispatch]);

	if (isChecking) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-lg">Loading...</div>
			</div>
		);
	}

	if (!authToken || isLoginRoute) {
		return <Login />;
	}

	if (!isOnboardingCompleted) {
		return null;
	}

	return <SocketProvider>{children}</SocketProvider>;
};

export default PrivateRoute;
